import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Brain, AlertTriangle, CheckCircle, Clock, Zap, CloudRain, Wrench,
  Users, Package, Shield, ChevronRight, Loader2, TrendingUp, BarChart3,
  XCircle, ArrowRight, Eye, RefreshCw, AlertCircle, ArrowLeft
} from 'lucide-react';

const FLAG_ICONS = {
  weather: CloudRain,
  plant: Wrench,
  labour: Users,
  material: Package,
  access: Shield,
  info: AlertCircle,
  instruction: AlertCircle,
  inspection: Eye,
  recurring: RefreshCw,
};

const SEVERITY_BADGE = {
  high: { cls: 'bg-red-100 text-red-700 border-red-200', label: 'High' },
  medium: { cls: 'bg-amber-100 text-amber-700 border-amber-200', label: 'Medium' },
  low: { cls: 'bg-blue-100 text-blue-700 border-blue-200', label: 'Low' },
};

const STATUS_BADGE = {
  new: { cls: 'bg-red-50 text-red-600', label: 'New' },
  acknowledged: { cls: 'bg-amber-50 text-amber-600', label: 'Acknowledged' },
  actioned: { cls: 'bg-emerald-50 text-emerald-600', label: 'Actioned' },
  dismissed: { cls: 'bg-slate-100 text-slate-500', label: 'Dismissed' },
};

export default function DelayDetectionPage() {
  const { user } = useAuth();
  const { projectId: selectedProject } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [dashboard, setDashboard] = useState(null);
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [filterSeverity, setFilterSeverity] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterType, setFilterType] = useState('');
  const [diaries, setDiaries] = useState([]);
  const [analyzeLoading, setAnalyzeLoading] = useState(false);

  useEffect(() => { loadProject(); }, [selectedProject]);
  useEffect(() => { if (selectedProject) { loadDashboard(); loadFlags(); loadDiaries(); } }, [selectedProject, filterSeverity, filterStatus, filterType]);

  const loadProject = async () => {
    try {
      const r = await api.get(`/projects/${selectedProject}`);
      setProject(r.data);
    } catch (e) { console.error(e); }
  };

  const loadDashboard = async () => {
    try {
      const r = await api.get(`/delay-detection/dashboard/${selectedProject}`);
      setDashboard(r.data);
    } catch (e) { console.error(e); }
  };

  const loadFlags = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterSeverity) params.append('severity', filterSeverity);
      if (filterStatus) params.append('status', filterStatus);
      if (filterType) params.append('flag_type', filterType);
      const r = await api.get(`/delay-detection/flags/${selectedProject}?${params}&limit=100`);
      setFlags(r.data.flags || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadDiaries = async () => {
    try {
      const r = await api.get(`/diary/projects/${selectedProject}?limit=10`);
      setDiaries(r.data.diaries || []);
    } catch (e) { console.error(e); }
  };

  const analyzeDiary = async (diaryId) => {
    setAnalyzeLoading(true);
    try {
      const r = await api.post(`/delay-detection/analyze/${diaryId}`);
      toast.success(`Analysis complete: ${r.data.total_flags} flags detected`);
      loadDashboard();
      loadFlags();
    } catch (e) { toast.error('Analysis failed'); }
    setAnalyzeLoading(false);
  };

  const analyzeAllRecent = async () => {
    setAnalyzeLoading(true);
    let totalFlags = 0;
    for (const d of diaries.slice(0, 5)) {
      try {
        const r = await api.post(`/delay-detection/analyze/${d.id}`);
        totalFlags += r.data.total_flags;
      } catch (e) { console.error(e); }
    }
    toast.success(`Analyzed ${Math.min(diaries.length, 5)} diaries: ${totalFlags} flags`);
    loadDashboard();
    loadFlags();
    setAnalyzeLoading(false);
  };

  const updateFlagStatus = async (flagId, status) => {
    setActionLoading(flagId);
    try {
      await api.put(`/delay-detection/flags/${flagId}/status?status=${status}`);
      toast.success(`Flag ${status}`);
      loadFlags();
      loadDashboard();
    } catch (e) { toast.error('Update failed'); }
    setActionLoading(null);
  };

  const createEventFromFlag = async (flagId) => {
    setActionLoading(flagId);
    try {
      const r = await api.post(`/delay-detection/flags/${flagId}/create-event`);
      toast.success(`Delay event created: ${r.data.event.title}`);
      loadFlags();
      loadDashboard();
    } catch (e) { toast.error('Failed to create event'); }
    setActionLoading(null);
  };

  const currentProject = project;

  return (
    <DashboardLayout>
      <div className="space-y-6 p-4 md:p-6" data-testid="delay-detection-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(`/projects/${selectedProject}`)} className="p-2 rounded-lg hover:bg-slate-100"><ArrowLeft className="h-5 w-5" /></button>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                <Brain className="h-6 w-6 text-violet-600" /> AI Delay Detection Engine
              </h1>
              <p className="text-sm text-slate-500 mt-1">{project?.name || ''}</p>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button onClick={analyzeAllRecent} disabled={analyzeLoading} className="bg-violet-600 hover:bg-violet-700" data-testid="analyze-all-btn">
              {analyzeLoading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Zap className="h-4 w-4 mr-1" />} Analyze Recent
            </Button>
          </div>
        </div>

        {/* Dashboard Stats */}
        {dashboard && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Card className="border-l-4 border-l-red-500"><CardContent className="p-4">
              <p className="text-xs text-slate-500">New Flags</p>
              <p className="text-2xl font-bold text-red-600">{dashboard.new}</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-amber-500"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Total Flags</p>
              <p className="text-2xl font-bold text-slate-900">{dashboard.total_flags}</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-emerald-500"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Actioned</p>
              <p className="text-2xl font-bold text-emerald-600">{dashboard.actioned}</p>
            </CardContent></Card>
            <Card className="border-l-4 border-l-slate-400"><CardContent className="p-4">
              <p className="text-xs text-slate-500">Dismissed</p>
              <p className="text-2xl font-bold text-slate-500">{dashboard.dismissed}</p>
            </CardContent></Card>
          </div>
        )}

        {/* By Type & Severity Breakdown */}
        {dashboard && (dashboard.by_type && Object.keys(dashboard.by_type).length > 0) && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">By Type</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(dashboard.by_type).map(([type, count]) => {
                  const Icon = FLAG_ICONS[type] || AlertTriangle;
                  return (
                    <div key={type} className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Icon className="h-4 w-4 text-slate-500" /><span className="text-sm capitalize">{type}</span></div>
                      <Badge variant="outline">{count}</Badge>
                    </div>
                  );
                })}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2"><CardTitle className="text-sm">By Severity</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {Object.entries(dashboard.by_severity).map(([sev, count]) => (
                  <div key={sev} className="flex items-center justify-between">
                    <Badge className={`${(SEVERITY_BADGE[sev] || SEVERITY_BADGE.medium).cls} text-xs`}>{sev}</Badge>
                    <span className="font-bold text-sm">{count}</span>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Recent Diaries (for manual analysis) */}
        {diaries.length > 0 && (
          <Card>
            <CardHeader className="pb-2"><CardTitle className="text-sm">Recent Diaries — Run Analysis</CardTitle></CardHeader>
            <CardContent>
              <div className="flex gap-2 flex-wrap">
                {diaries.slice(0, 7).map(d => (
                  <Button key={d.id} onClick={() => analyzeDiary(d.id)} variant="outline" size="sm" disabled={analyzeLoading} className="gap-1" data-testid={`analyze-diary-${d.id}`}>
                    <Zap className="h-3 w-3" /> {d.date}
                    {d.delay_flags_count > 0 && <Badge className="bg-red-100 text-red-700 text-[10px] ml-1">{d.delay_flags_count}</Badge>}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          <Select value={filterSeverity || 'all'} onValueChange={v => setFilterSeverity(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Severity" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Severity</SelectItem><SelectItem value="high">High</SelectItem><SelectItem value="medium">Medium</SelectItem><SelectItem value="low">Low</SelectItem></SelectContent>
          </Select>
          <Select value={filterStatus || 'all'} onValueChange={v => setFilterStatus(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-36"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Status</SelectItem><SelectItem value="new">New</SelectItem><SelectItem value="acknowledged">Acknowledged</SelectItem><SelectItem value="actioned">Actioned</SelectItem><SelectItem value="dismissed">Dismissed</SelectItem></SelectContent>
          </Select>
          <Select value={filterType || 'all'} onValueChange={v => setFilterType(v === 'all' ? '' : v)}>
            <SelectTrigger className="w-32"><SelectValue placeholder="Type" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All Types</SelectItem><SelectItem value="weather">Weather</SelectItem><SelectItem value="plant">Plant</SelectItem><SelectItem value="labour">Labour</SelectItem><SelectItem value="material">Material</SelectItem><SelectItem value="access">Access</SelectItem><SelectItem value="recurring">Recurring</SelectItem></SelectContent>
          </Select>
        </div>

        {/* Flags List */}
        <div className="space-y-3" data-testid="flags-list">
          {loading ? (
            <div className="text-center py-12"><Loader2 className="h-8 w-8 animate-spin mx-auto text-slate-400" /></div>
          ) : flags.length === 0 ? (
            <Card><CardContent className="p-12 text-center">
              <Brain className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No delay flags detected yet.</p>
              <p className="text-xs text-slate-400 mt-1">Create diary entries and run analysis to detect potential delays.</p>
            </CardContent></Card>
          ) : flags.map(flag => {
            const Icon = FLAG_ICONS[flag.type] || AlertTriangle;
            const sev = SEVERITY_BADGE[flag.severity] || SEVERITY_BADGE.medium;
            const stat = STATUS_BADGE[flag.status] || STATUS_BADGE.new;
            const isLoading = actionLoading === flag.id;
            return (
              <Card key={flag.id} className={`transition-colors ${flag.status === 'new' ? 'border-l-4 border-l-red-500' : flag.status === 'actioned' ? 'border-l-4 border-l-emerald-500' : ''}`} data-testid={`flag-${flag.id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex items-start gap-3 flex-1">
                      <div className={`p-2 rounded-lg ${flag.severity === 'high' ? 'bg-red-100' : flag.severity === 'medium' ? 'bg-amber-100' : 'bg-blue-100'}`}>
                        <Icon className={`h-5 w-5 ${flag.severity === 'high' ? 'text-red-600' : flag.severity === 'medium' ? 'text-amber-600' : 'text-blue-600'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <h3 className="text-sm font-bold text-slate-900">{flag.title}</h3>
                          <Badge className={`${sev.cls} text-[10px]`}>{sev.label}</Badge>
                          <Badge className={`${stat.cls} text-[10px]`}>{stat.label}</Badge>
                        </div>
                        <p className="text-sm text-slate-600">{flag.description}</p>
                        {flag.contractual_trigger && (
                          <p className="text-xs text-violet-600 mt-1 flex items-center gap-1"><Zap className="h-3 w-3" /> {flag.contractual_trigger}</p>
                        )}
                        {flag.time_impact && <p className="text-xs text-slate-400 mt-0.5">Time impact: {flag.time_impact}</p>}
                        <p className="text-[10px] text-slate-400 mt-1">Detected: {new Date(flag.detected_at).toLocaleString()} | Diary: {flag.diary_date}</p>
                      </div>
                    </div>
                    {flag.status !== 'actioned' && flag.status !== 'dismissed' && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {flag.suggested_action === 'create_delay_event' && (
                          <Button onClick={() => createEventFromFlag(flag.id)} size="sm" className="bg-red-600 hover:bg-red-700 gap-1" disabled={isLoading} data-testid={`create-event-${flag.id}`}>
                            {isLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <ArrowRight className="h-3 w-3" />} Create Event
                          </Button>
                        )}
                        <Button onClick={() => updateFlagStatus(flag.id, 'acknowledged')} size="sm" variant="outline" disabled={isLoading} data-testid={`ack-flag-${flag.id}`}>
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button onClick={() => updateFlagStatus(flag.id, 'dismissed')} size="sm" variant="ghost" disabled={isLoading} data-testid={`dismiss-flag-${flag.id}`}>
                          <XCircle className="h-3 w-3 text-slate-400" />
                        </Button>
                      </div>
                    )}
                    {flag.status === 'actioned' && flag.linked_event_id && (
                      <Badge className="bg-emerald-100 text-emerald-700 text-xs gap-1"><CheckCircle className="h-3 w-3" /> Event Created</Badge>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </DashboardLayout>
  );
}
